import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";

const formatDate = (iso) => moment(iso).format("YYYY.MM.DD HH:mm");

const buildSummary = (filters) => {
  const displays = filters.map((f) => f.display).filter(Boolean);
  if (!displays.length) return "—";
  const head = displays.slice(0, 3).join(", ");
  const more = displays.length > 3 ? ` +${displays.length - 3} további` : "";
  return `${head}${more}`;
};

const PresetManagerModal = ({
  open,
  onClose,
  presets,
  currentFilterCount,
  onSave,
  onLoad,
  onDelete,
}) => {
  const [name, setName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmOverwrite, setConfirmOverwrite] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSaveClick = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const existing = presets.find((p) => p.name === trimmed);
    if (existing) {
      setConfirmOverwrite(trimmed);
      return;
    }
    try {
      onSave(trimmed);
      setName("");
      showSnackbar(`Szűrőbeállítás mentve: ${trimmed}`);
    } catch (err) {
      showSnackbar(`Nem sikerült menteni: ${err.message}`, "error");
    }
  };

  const handleConfirmOverwrite = () => {
    const trimmed = confirmOverwrite;
    setConfirmOverwrite(null);
    try {
      onSave(trimmed);
      setName("");
      showSnackbar(`Szűrőbeállítás felülírva: ${trimmed}`);
    } catch (err) {
      showSnackbar(`Nem sikerült menteni: ${err.message}`, "error");
    }
  };

  const handleLoadClick = (preset) => {
    onLoad(preset);
    showSnackbar(`Szűrőbeállítás betöltve: ${preset.name}`);
  };

  const handleDeleteClick = (preset) => {
    setConfirmDelete(preset);
  };

  const handleConfirmDelete = () => {
    const preset = confirmDelete;
    setConfirmDelete(null);
    onDelete(preset.id);
    showSnackbar("Szűrőbeállítás törölve");
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Mentett szűrők
          <IconButton
            aria-label="bezárás"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Új szűrőbeállítás mentése
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                fullWidth
                size="small"
                placeholder="Név (pl. Budapest IT magyar)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveClick}
                disabled={!name.trim() || currentFilterCount === 0}
              >
                Mentés
              </Button>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Mentésre kerül: {currentFilterCount} aktív szűrőfeltétel
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1">
              Mentett szűrőbeállítások ({presets.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Keresés név szerint…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          {presets.length === 0 ? (
            <Typography color="text.secondary">
              Nincs még mentett szűrőbeállítás.
            </Typography>
          ) : (
            (() => {
              const q = searchQuery.trim().toLowerCase();
              const visible = [...presets]
                .reverse()
                .filter((p) => !q || p.name.toLowerCase().includes(q));
              if (visible.length === 0) {
                return (
                  <Typography color="text.secondary">
                    Nincs találat a keresésre.
                  </Typography>
                );
              }
              return (
                <Stack spacing={1}>
                  {visible.map((preset) => (
                    <Card key={preset.id} variant="outlined">
                      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" noWrap>
                              {preset.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              Mentve: {formatDate(preset.createdAt)}
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {buildSummary(preset.filters)}
                            </Typography>
                          </Box>
                          <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => handleLoadClick(preset)}
                            >
                              Betöltés
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteClick(preset)}
                            >
                              Törlés
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              );
            })()
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Bezárás</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmOverwrite)}
        onClose={() => setConfirmOverwrite(null)}
        aria-labelledby="preset-overwrite-title"
      >
        <DialogTitle id="preset-overwrite-title">Felülírás</DialogTitle>
        <DialogContent>
          <Typography>
            Már létezik <strong>{confirmOverwrite}</strong> nevű szűrőbeállítás.
            Felülírja?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverwrite(null)}>Mégsem</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmOverwrite}
          >
            Felülírás
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="preset-delete-title"
      >
        <DialogTitle id="preset-delete-title">Törlés megerősítése</DialogTitle>
        <DialogContent>
          <Typography>
            Biztosan törlöd: <strong>{confirmDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Mégsem</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

PresetManagerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  presets: PropTypes.array.isRequired,
  currentFilterCount: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default PresetManagerModal;
